import { GoogleLogin } from '@react-oauth/google';
import { getClientIdMicrosoft } from 'app/config/config';
import { defaultConfig } from 'app/config/environment';
import { recoverAccount } from 'core/crud/auth.crud';
import useScopes from 'core/hooks/useScopes';
import TextHtml from 'core/modules/atoms/TextHtml';
import ForgotPasswordForm from 'core/modules/components/forms/ForgotPasswordForm/ForgotPasswordForm';
import * as coreEntities from 'core/store/index';
import { successResponse } from 'core/utils/utils';
import i18next from 'i18next';
import * as Msal from 'msal';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { withTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { Dialog, InputCheckbox, InputText, FormField } from 'ui';
import initLayoutConfig from '../../../../app/config/layout/LayoutConfig';
import Button from '../../atoms/Button';
import LineWithText from '../../atoms/LineWithText';
import LoginOraculo from '../ssoOraculo/LoginOraculo';

const POLITICS = { LEGAL: 'legal', PRIVACY: 'privacy' };
const FORGOT_PASSWORD_STATE = { INITIAL: 'initial', LOADING: 'loading', SUCCESS: 'success' };

const LoginForm = (props) => {
  const { addToast } = useToasts();
  const { systemRequirements } = useScopes();
  const { register, handleSubmit, watch, errors } = useForm();
  const { t, legal, privacy, onSubmit, onSSO, errorMsg, loading, showRememberMe, showForgotPassword } = props;
  const client = useSelector((state) => coreEntities.organization.selectors.getClient(state));
  const language = useSelector((state) => coreEntities.i18n.selectors.getLanguage(state));

  const [showLegal, setShowLegal] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showSystem, setShowSystem] = useState(false);
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const [forgotPasswordState, setForgotPasswordState] = useState(FORGOT_PASSWORD_STATE.INITIAL);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [buttonWidth, setButtonWidth] = useState('300px'); // Ancho predeterminado
  const [visible, setVisible] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth);
      if (buttonRef.current !== null) {
        const width = buttonRef.current.offsetWidth;
        setButtonWidth(`${width}px`);
      }
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  });

  useEffect(() => {
    if (buttonRef.current) {
      const width = buttonRef.current.offsetWidth;
      setButtonWidth(`${width}px`);
    }
  }, []);

  const onLogin = (data) => {
    onSubmit && onSubmit(data.email, data.password);
  };

  const onLoginSSO = (token) => {
    onSSO && onSSO(token, 'google');
  };

  const onLoginMicrosoft = (onSubmitSSO, onErrorSSO) => {
    const clientId = getClientIdMicrosoft(window.location.origin);
    const config = { auth: { clientId, redirectUri: window.location.origin } };

    let myMSALObj = new Msal.UserAgentApplication(config);
    let requestObj = { scopes: ['user.read'], prompt: 'select_account' };

    myMSALObj
      .loginPopup(requestObj)
      .then(async function (loginResponse) {
        myMSALObj
          .acquireTokenSilent(requestObj)
          .then(async (response) => {
            const user = {
              name: response.account.name,
              email: response.account.userName,
              sso_token: response.accessToken,
              identity_provider: 'microsoft',
            };
            //onSubmitSSO(user);
            onSSO && onSSO(user.sso_token, user.identity_provider);
          })
          .catch(function (error) {
            console.error('SSO ms error: ', error);
            myMSALObj
              .acquireTokenPopup(requestObj)
              .then(async function (response) {
                const user = {
                  name: response.account.name,
                  email: response.account.userName,
                  sso_token: response.accessToken,
                  identity_provider: 'microsoft',
                };
                //onSubmitSSO(user);
                onSSO && onSSO(user.sso_token, user.identity_provider);
              })
              .catch(function (error) {
                onErrorSSO && onErrorSSO(error);
              });
          });
      })
      .catch(function (error) {
        onErrorSSO && onErrorSSO(error);
      });
  };

  const closeShowForgotPasswordDialog = () => {
    setShowForgotPasswordDialog(false);
    setForgotPasswordState(FORGOT_PASSWORD_STATE.INITIAL);
  };

  async function onSendChangePassword(email) {
    setForgotPasswordState(FORGOT_PASSWORD_STATE.LOADING);
    const response = await recoverAccount(email, { link: defaultConfig.BASE_URL_ + 'recover-password?token=[TOKEN]&lang=[LANG]' });
    if (successResponse(response)) {
      setForgotPasswordState(FORGOT_PASSWORD_STATE.SUCCESS);
    } else {
      setForgotPasswordState(FORGOT_PASSWORD_STATE.INITIAL);
      addToast(t(`login:USER_NOT_FOUND`), {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  }

  function onShowPolitics(selection) {
    const politics = selection === POLITICS.PRIVACY ? privacy : legal;
    if (politics?.endsWith('pdf')) {
      if (selection === POLITICS.PRIVACY) window.open(defaultConfig.BASE_URL_ + 'privacy', '_blank');
      if (selection === POLITICS.LEGAL) window.open(defaultConfig.BASE_URL_ + 'terms', '_blank');
    } else {
      if (selection === POLITICS.PRIVACY) setShowPrivacy(selection);
      if (selection === POLITICS.LEGAL) setShowLegal(selection);
    }
  }

  return (
    <div className="login-form">
      {forgotPasswordState === FORGOT_PASSWORD_STATE.LOADING && <Loading />}

      <div className="login-form__form-container">
        <form onSubmit={handleSubmit(onLogin)} className="login-form__form">
          <div className="login-form__item">
            <h1 className="login-form__title">{t('login:Log in')}</h1>
          </div>
          <div className="login-form__item">
            <FormField
              error={!!errors.email}
              errorMessage={t('auth:Enter your username')}
              label={t('auth:User')}
            >
              <InputText
                icon="user"
                iconLeft
                name="email"
                placeholder={t('auth:Username')}
                referer={register({ required: true })}
              />
            </FormField>
          </div>
          <div className="login-form__item">
            <FormField
              error={!!errors.password}
              errorMessage={t('auth:Enter your password')}
              label={t('auth:Password')}
            >
              <InputText
                icon={visible ? 'visible' : 'visible-off'}
                iconLeft
                name="password"
                onClickIcon={() => setVisible(!visible)}
                placeholder={t('auth:Password')}
                referer={register({ required: true })}
                type={visible ? 'text' : 'password'}
              />
            </FormField>
          </div>
          <div className="login-form__item login-form__item--subactions">
            {showRememberMe && (
              <div className="login-form__checkbox-container">
                <InputCheckbox label={t('Remember me')} />
              </div>
            )}
            {showForgotPassword && (
              <Button
                onClick={() => setShowForgotPasswordDialog(true)}
                text={t('Forgot your password?')}
                accessibilityOnly
                className="login-form__text login-form__text--forget"
                ariaLabel={t('buttons:Forgot your password')}
                ariaHasPopup="dialog"
                ariaControls="dialog-forgot-password"
              />
            )}
          </div>
          <div className="login-form__item">
            <Button testid="btn-login" text={t('Log in')} loading={loading} type="submit" />
          </div>
          {
            <>
              <div className="login-form__item">
                <LineWithText text={t('Or')} />
              </div>
              <div className="login-form__third-parties">
                <div className="login-form__item login-form__item--google">
                  <GoogleLogin
                    width={buttonWidth}
                    text="signin_with"
                    onSuccess={(credentialResponse) => {
                      onLoginSSO(credentialResponse.credential);
                    }}
                    onError={(response) => {
                      console.log('google fail ', response);
                    }}
                    locale={i18next.language}
                  />
                </div>
                <div className="login-form__item" ref={buttonRef}>
                  <Button
                    testid="btn-sso-microsoft"
                    color="white"
                    text={t('login:Login with Microsoft')}
                    icon="microsoft"
                    iconPosition="left"
                    onClick={onLoginMicrosoft}
                  />
                </div>

                {initLayoutConfig.login.showOraculo && (
                  <div className="login-form__item">
                    <LoginOraculo text={t('login:Login with Polp')} />
                  </div>
                )}
              </div>
            </>
          }
          <Button
            to="/auth/signup"
            accessibilityOnly={true}
            className="login-form__signup-button"
            text={
              <span className="login-form__text">
                {t('If you don’t have an account yet,')}
                <span className="login-form__link"> {t('register here')}</span>
              </span>
            }
          ></Button>

          <div className="login-form__politics">
            {legal && (
              <Button
                onClick={() => onShowPolitics(POLITICS.LEGAL)}
                text={t('login:Legal advice')}
                accessibilityOnly
                className="login-form__legal-advice"
                ariaLabel={t('buttons:View legal advice')}
                ariaHasPopup="dialog"
                ariaControls="dialog-legal-advice"
              />
            )}
            {privacy && (
              <Button
                onClick={() => onShowPolitics(POLITICS.PRIVACY)}
                text={t('login:Privacy')}
                accessibilityOnly
                className="login-form__privacy"
                ariaLabel={t('buttons:View privacy policy')}
                ariaHasPopup="dialog"
                ariaControls="dialog-privacy"
              />
            )}
            <Button
              onClick={() => setShowSystem(true)}
              text={t('login:System Requirements')}
              accessibilityOnly
              className="login-form__system-requirements"
              ariaLabel={t('buttons:View system requirements')}
              ariaHasPopup="dialog"
              ariaControls="dialog-system-requirements"
            />
          </div>
        </form>
      </div>

      <Dialog
        id="dialog-legal-advice"
        size="xl"
        className="dialog-terms"
        title={t('login:Legal advice').toUpperCase()}
        isOpen={showLegal}
        onClose={() => setShowLegal(false)}
      >
        <TextHtml text={legal} />
      </Dialog>

      <Dialog
        id="dialog-privacy"
        size="xl"
        className="dialog-terms"
        title={t('login:Privacy').toUpperCase()}
        isOpen={showPrivacy}
        onClose={() => setShowPrivacy(false)}
      >
        <TextHtml text={privacy} />
      </Dialog>

      <Dialog
        id="dialog-system-requirements"
        size="xl"
        className="dialog-terms"
        title={t('login:System Requirements').toUpperCase()}
        isOpen={showSystem}
        onClose={() => setShowSystem(false)}
      >
        <TextHtml text={systemRequirements} />
      </Dialog>

      <Dialog
        id="dialog-forgot-password"
        size="s"
        className="dialog-forgot-password"
        title={t('login:Recover password')}
        isOpen={showForgotPasswordDialog}
        onClose={closeShowForgotPasswordDialog}
      >
        <ForgotPasswordForm t={t} state={forgotPasswordState} onCancel={closeShowForgotPasswordDialog} onSuccess={onSendChangePassword} />
      </Dialog>
    </div>
  );
};

export default withTranslation(['login', 'auth', 'common', 'recover'])(LoginForm);
